import React from 'react'
import { getDay } from 'date-fns'
import { Element } from 'react-bulma-components'
import { WeekSummary } from '../../../../backend/src/services/resources/missions/missions.summary.service.types'
import {
  SummaryData,
  SummaryDayData,
} from '../../../../backend/src/services/resources/period-summaries/period-summary.types'
import { WorkPeriodWithSummary } from '../../api/work-periods'
import Table, { ExtendedColumn } from '../table'
import { formatDayExtended, getWeekdayMondayBased, type WeekDay } from '../../utils/date'
import { getDocIdAsString } from '../../utils/documents'
import { isAuthorizedFor } from '../protected/protected'
import WorkPeriodLink from './work-period-link'
import {
  getEmployerRuleFields,
  getWorkPeriodFields,
  getWorkPeriodMetaFields,
} from './table-field-definitions'

export interface DailyWorkPeriodsTableProps {
  /** One row per work period; typically all periods for a single mission or user week */
  workPeriods: WorkPeriodWithSummary[]
  /** When set, adds one column per rule (same as summary week table) */
  employerRules?: WeekSummary['employerRules']
  /** Per weekday totals (0 = Monday … 6 = Sunday) to resolve employer rule values */
  dayTotals?: SummaryData['days']
  /**
   * When rows span multiple weeks, use this to pick the correct `totals.days[weekday]` slice per work period
   * (overrides `dayTotals` for employer columns when provided).
   */
  resolveEmployerRuleDay?: (workPeriod: WorkPeriodWithSummary) => SummaryDayData | undefined
  /** When true, only Début / Fin columns (and employer rules hidden) */
  minimalMode?: boolean
  noDataMessage?: string
  className?: string
  dataTest?: string
  /** When true, shows the theoretical schedule column */
  showTheoreticalSchedule?: boolean
}

/**
 * Table with one row per work period and a flat header (Jour, Début, Fin, …, meta columns).
 * Reuses {@link getWorkPeriodFields}, {@link getEmployerRuleFields}, {@link getWorkPeriodMetaFields}.
 * For users with role `employerMember`, the day column is a {@link WorkPeriodLink} to the period detail.
 */
const DailyWorkPeriodsTable: React.FC<DailyWorkPeriodsTableProps> = ({
  workPeriods,
  employerRules,
  dayTotals,
  resolveEmployerRuleDay,
  minimalMode = false,
  noDataMessage,
  className,
  dataTest = 'daily-work-periods-table',
  showTheoreticalSchedule = true,
}) => {
  const columns = React.useMemo((): ExtendedColumn<WorkPeriodWithSummary>[] => {
    const fields = getWorkPeriodFields({ showTheoreticalSchedule }).filter(
      f => !f.minimalOnly || !minimalMode,
    )

    const baseColumns: ExtendedColumn<WorkPeriodWithSummary>[] = [
      {
        id: 'calendar-day',
        Header: <Element mt={1}>Jour</Element>,
        Cell: ({ row }) => {
          const wp = row.original
          const dateLabel = formatDayExtended(new Date(wp.start.date))
          const strikethroughStyle =
            wp.isCancelled || wp.isAbandoned
              ? { textDecoration: 'line-through', textDecorationThickness: '1px' }
              : {}

          if (isAuthorizedFor(['employerMember'])) {
            return (
              <Element style={{ whiteSpace: 'normal' }} display="flex" alignItems="center">
                <WorkPeriodLink workPeriod={wp} textColor="link" style={strikethroughStyle}>
                  <Element renderAs="span">{dateLabel}</Element>
                </WorkPeriodLink>
              </Element>
            )
          }

          return (
            <Element
              renderAs="span"
              textColor="dark"
              style={{
                whiteSpace: 'normal',
                ...strikethroughStyle,
              }}
              display="flex"
              alignItems="center"
            >
              {dateLabel}
            </Element>
          )
        },
      },
      ...fields.map(field => ({
        id: field.id,
        Header: <Element mt={1}>{field.header}</Element>,
        Cell: ({ row }: { row: { original: WorkPeriodWithSummary } }) => (
          <Element style={{ whiteSpace: 'nowrap' }} display="flex" alignItems="center">
            {field.render(row.original)}
          </Element>
        ),
      })),
    ]

    const employerColumns: ExtendedColumn<WorkPeriodWithSummary>[] =
      minimalMode || !employerRules?.length
        ? []
        : getEmployerRuleFields(employerRules).map(field => ({
            id: `employer-${field.id}`,
            Header: <Element mt={1}>{field.header}</Element>,
            Cell: ({ row }: { row: { original: WorkPeriodWithSummary } }) => {
              const wp = row.original
              const weekDayNumber = getWeekdayMondayBased(
                getDay(new Date(wp.start.date)) as WeekDay,
              )
              const dayTotal = resolveEmployerRuleDay?.(wp) ?? dayTotals?.[weekDayNumber]
              const rule = field.employerRule
              const value =
                rule.kind === 'goal'
                  ? (dayTotal?.employerData.goals[getDocIdAsString(rule.ruleId)] ?? null)
                  : (dayTotal?.employerData.durations[getDocIdAsString(rule.ruleId)] ?? null)

              return (
                <Element style={{ whiteSpace: 'nowrap' }} display="flex" alignItems="center">
                  {field.render(wp, value)}
                </Element>
              )
            },
          }))

    const metaColumns: ExtendedColumn<WorkPeriodWithSummary>[] = getWorkPeriodMetaFields()
      .filter(f => !f.minimalOnly || !minimalMode)
      .map(field => ({
        id: field.id,
        Header: <Element mt={1}>{field.header}</Element>,
        Cell: ({ row }: { row: { original: WorkPeriodWithSummary } }) => (
          <Element style={{ whiteSpace: 'nowrap' }} display="flex" alignItems="center">
            {field.render(row.original)}
          </Element>
        ),
      }))

    return [...baseColumns, ...employerColumns, ...metaColumns]
  }, [employerRules, dayTotals, resolveEmployerRuleDay, minimalMode])

  return (
    <Table<WorkPeriodWithSummary>
      columns={columns}
      data={workPeriods}
      noDataMessage={noDataMessage ?? 'Aucune période à afficher'}
      className={className}
      dataTest={dataTest}
    />
  )
}

export default DailyWorkPeriodsTable
